import { ref, watch, computed } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import store from '@/store'
import router from '@/router/index'
import ToastificationContentCustom from '../ToastificationContentCustom.vue'

// Notification

export default function useWorkFlowList() {
  // Use toast
  const toast = useToast()
  const route = router.history.current

  const refAllExpenseCategoryTable = ref(null)

  // Table Handlers
  const tableColumns = [
    {
      key: 'stringID',
      label: 'id',
      sortable: true,
    },
    {
      key: 'type',
      label: 'expense category type',
      sortable: true,
    },
    {
      key: 'name',
      label: 'expense category',
      sortable: true,
    },
    {
      key: 'code',
      label: 'budget code',
      sortable: true,
    },
    {
      key: 'departments',
      label: 'usable by department',
      sortable: false,
    },
    {
      key: 'createdAt',
      label: 'created on',
      sortable: true,
    },
    { key: 'status', sortable: true },
    { key: 'actions' },
  ]
  const perPage = ref(route.query.perPage ?? process.env.VUE_APP_PER_PAGE_CONTENT)
  const totalExpenseCategories = ref(0)
  const currentPage = ref(route.query.page ?? 1)
  const perPageOptions = [10, 25, 50, 100]
  const searchQuery = ref(route.query.search ?? '')
  const expenseCategoryTypeFilter = ref(route.query.categoryType ?? 'Operating Expenses')
  const sortBy = ref(route.query.sortBy ?? 'stringID')
  const isSortDirDesc = ref(route.query.sortDesc ? (route.query.sortDesc === 'true') : true)
  const toDeletedID = ref(null)
  const deletedExpenseCategoryName = ref('')
  const expenseCategoryData = ref([])
  let initialLoad = true

  const dataMeta = computed(() => {
    const localItemsCount = refAllExpenseCategoryTable.value ? refAllExpenseCategoryTable.value.localItems.length : 0
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalExpenseCategories.value,
    }
  })

  const refetchData = () => {
    refAllExpenseCategoryTable.value.refresh()
  }

  watch([searchQuery, expenseCategoryTypeFilter], () => {
    currentPage.value = 1
    refetchData()
  }, { deep: true })

  watch([currentPage], () => {
    refetchData()
  }, { deep: true })

  const fetchExpenseCategories = (ctx, callback) => {
    store
      .dispatch('app-all-expense-categories/fetchExpenseCategories', {
        search: searchQuery.value,
        perPage: perPage.value,
        page: currentPage.value,
        sortBy: sortBy.value,
        sortDesc: isSortDirDesc.value,
        categoryType: expenseCategoryTypeFilter.value,
      },
      { root: true })
      .then(response => {
        const { expenseCategories, total } = response.data
        expenseCategoryData.value = expenseCategories
        callback(expenseCategories)
        totalExpenseCategories.value = total
        if (!initialLoad) {
          router.replace({
            query: {
              search: searchQuery.value,
              perPage: perPage.value,
              page: currentPage.value,
              sortBy: sortBy.value,
              sortDesc: isSortDirDesc.value,
              categoryType: expenseCategoryTypeFilter.value,
            },
          }).catch(() => {})
        }
        initialLoad = false
      })
      .catch(error => {
        toast({
          component: ToastificationContent,
          props: {
            title: error.data.message || process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  const deleteExpenseCategory = () => {
    store
      .dispatch('app-all-expense-categories/deleteExpenseCategory', {
        id: toDeletedID.value,
      },
      { root: true })
      .then(() => {
        refetchData()
        const toastMessage = `${deletedExpenseCategoryName.value} has been deleted.`
        toast({
          component: ToastificationContentCustom,
          props: {
            title: 'Expense Category is deleted',
            text: toastMessage,
            icon: 'Trash2Icon',
            variant: 'danger',
          },
        })
      })
      .catch(error => {
        toast({
          component: ToastificationContent,
          props: {
            title: error.data.message || process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  // *===============================================---*
  // *--------- UI ---------------------------------------*
  // *===============================================---*

  const resolveExpenseCategoryStatusVariant = status => {
    if (status === 'active') return 'success'
    if (status === 'view only') return 'warning'
    return 'primary'
  }

  return {
    fetchExpenseCategories,
    tableColumns,
    perPage,
    currentPage,
    totalExpenseCategories,
    dataMeta,
    perPageOptions,
    searchQuery,
    expenseCategoryTypeFilter,
    sortBy,
    isSortDirDesc,
    refAllExpenseCategoryTable,

    resolveExpenseCategoryStatusVariant,
    refetchData,

    toDeletedID,
    deletedExpenseCategoryName,
    deleteExpenseCategory,
    expenseCategoryData,
  }
}
